import React, { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MapPin, Mountain, Droplets, TriangleAlert, Ruler, Pin, Trash2, Crosshair, X, Snowflake } from "lucide-react";

const MARS_TILE = "https://cartocdn-gusc.global.ssl.fastly.net/opmbuilder/api/v1/map/named/opm-mars-basemap-v0-2/all/{z}/{x}/{y}.png";
const MARS_RADIUS_KM = 3389.5;

export const CATEGORIES = [
    { id: "observation", label: "Observation", color: "#ff6b35" },
    { id: "crater", label: "Crater", color: "#a78bfa" },
    { id: "ice", label: "Ice deposit", color: "#22d3ee" },
    { id: "hazard", label: "Hazard", color: "#ef4444" },
    { id: "sample", label: "Sample site", color: "#22c55e" },
];
const catColor = (id) => (CATEGORIES.find((c) => c.id === id) || CATEGORIES[0]).color;
const catLabel = (id) => (CATEGORIES.find((c) => c.id === id) || CATEGORIES[0]).label;

const iceColor = (p) => (p >= 0.7 ? "#00e5ff" : p >= 0.4 ? "#00ff88" : "#ffb703");
const toLng = (lon) => (lon > 180 ? lon - 360 : lon);

const evidenceBadge = {
    "very high": "bg-ares-cyan/15 text-ares-cyan border-ares-cyan/40",
    high: "bg-ares-emerald/15 text-ares-emerald border-ares-emerald/40",
    moderate: "bg-ares-amber/15 text-ares-amber border-ares-amber/40",
    low: "bg-ares-muted/15 text-ares-muted border-ares-muted/40",
};

function marsDistanceKm(a, b) {
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b[0] - a[0]);
    const dLon = toRad(b[1] - a[1]);
    const lat1 = toRad(a[0]);
    const lat2 = toRad(b[0]);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 2 * MARS_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

function regionIcon(region, active) {
    const c = iceColor(region.ice_probability);
    return L.divIcon({
        className: "ares-region-marker",
        html: `<div style="position:relative;display:flex;align-items:center;justify-content:center">
            ${region.ice_probability >= 0.5 ? `<span style="position:absolute;width:30px;height:30px;border-radius:50%;background:${c}33;animation:radar-ping 2s ease-out infinite"></span>` : ""}
            <span style="position:relative;width:18px;height:18px;border-radius:50%;border:2px solid ${c};background:${active ? c : "rgba(8,9,13,0.85)"};box-shadow:0 0 8px ${c}99"></span>
        </div>`,
        iconSize: [18, 18], iconAnchor: [9, 9],
    });
}

function annotationIcon(category) {
    const c = catColor(category);
    return L.divIcon({
        className: "ares-annotation-marker",
        html: `<div style="transform:translate(-50%,-100%);display:flex;flex-direction:column;align-items:center">
            <span style="width:16px;height:16px;background:${c};border:2px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 0 10px ${c}"></span>
        </div>`,
        iconSize: [16, 24], iconAnchor: [0, 0],
    });
}

export default function GisMap() {
    const [regions, setRegions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [projects, setProjects] = useState([]);
    const [activeProjectId, setActiveProjectId] = useState("");
    const [mode, setMode] = useState(null); // null | 'add' | 'measure'
    const [layers, setLayers] = useState({ regions: true, annotations: true, ice: false });
    const [pending, setPending] = useState(null);
    const [form, setForm] = useState({ label: "", note: "", category: "observation" });
    const [measurePts, setMeasurePts] = useState([]);

    const mapEl = useRef(null);
    const mapRef = useRef(null);
    const regionLayerRef = useRef(null);
    const annoLayerRef = useRef(null);
    const iceLayerRef = useRef(null);
    const measureLayerRef = useRef(null);
    const modeRef = useRef(null);

    const activeProject = projects.find((p) => p.id === activeProjectId);
    const annotations = activeProject?.annotations || [];
    const measureTotal = measurePts.reduce((sum, p, i) => (i === 0 ? 0 : sum + marsDistanceKm(measurePts[i - 1], p)), 0);

    useEffect(() => { modeRef.current = mode; }, [mode]);

    const refreshProjects = useCallback(async () => {
        const { data } = await api.get("/projects");
        setProjects(data);
        return data;
    }, []);

    const handleMapClick = useCallback((latlng) => {
        if (modeRef.current === "add") {
            setPending({ lat: +latlng.lat.toFixed(4), lng: +latlng.lng.toFixed(4) });
            setForm({ label: "", note: "", category: "observation" });
        } else if (modeRef.current === "measure") {
            setMeasurePts((pts) => [...pts, [latlng.lat, latlng.lng]]);
        }
    }, []);

    // init map
    useEffect(() => {
        if (mapRef.current || !mapEl.current) return;
        const map = L.map(mapEl.current, {
            center: [0, 0], zoom: 2, minZoom: 1, maxZoom: 6,
            worldCopyJump: false, maxBounds: [[-85, -180], [85, 180]], maxBoundsViscosity: 1.0,
        });
        L.tileLayer(MARS_TILE, { tileSize: 256, noWrap: true, maxNativeZoom: 5, maxZoom: 6, attribution: "OpenPlanetaryMap · NASA/USGS Mars basemap" }).addTo(map);
        iceLayerRef.current = L.layerGroup();
        regionLayerRef.current = L.layerGroup().addTo(map);
        annoLayerRef.current = L.layerGroup().addTo(map);
        measureLayerRef.current = L.layerGroup().addTo(map);
        mapRef.current = map;

        map.on("click", (e) => handleMapClick(e.latlng));

        api.get("/regions").then(({ data }) => { setRegions(data); setSelected(data[0]); });
        refreshProjects();
        setTimeout(() => map.invalidateSize(), 200);
        return () => { map.remove(); mapRef.current = null; };
    }, [refreshProjects, handleMapClick]);

    // ice heatmap layer
    useEffect(() => {
        const map = mapRef.current, lg = iceLayerRef.current;
        if (!map || !lg) return;
        lg.clearLayers();
        if (layers.ice) {
            regions.forEach((r) => {
                const center = [r.lat, toLng(r.lon)];
                const base = 450000 + r.ice_probability * 900000;
                [[1.6, 0.05], [1.1, 0.1], [0.65, 0.22]].forEach(([mult, op]) => {
                    L.circle(center, { radius: base * mult, stroke: false, fillColor: "#00e5ff", fillOpacity: op * (0.5 + r.ice_probability) }).addTo(lg);
                });
            });
            if (!map.hasLayer(lg)) lg.addTo(map);
        } else if (map.hasLayer(lg)) {
            map.removeLayer(lg);
        }
    }, [layers.ice, regions]);

    // region markers
    useEffect(() => {
        const lg = regionLayerRef.current;
        if (!lg) return;
        lg.clearLayers();
        if (!layers.regions) return;
        regions.forEach((r) => {
            const m = L.marker([r.lat, toLng(r.lon)], { icon: regionIcon(r, selected?.id === r.id) });
            m.bindTooltip(r.name, { direction: "top", offset: [0, -10], className: "font-mono" });
            m.on("click", (e) => {
                if (modeRef.current) { handleMapClick(e.latlng); return; }
                setSelected(r);
            });
            m.addTo(lg);
        });
    }, [regions, selected, layers.regions, handleMapClick]);

    // annotation markers
    const drawAnnotations = useCallback(() => {
        const lg = annoLayerRef.current;
        if (!lg) return;
        lg.clearLayers();
        if (!layers.annotations || !activeProject) return;
        annotations.forEach((a) => {
            const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
            const m = L.marker([a.lat, a.lon], { icon: annotationIcon(a.category) });
            const popup = document.createElement("div");
            popup.innerHTML = `<p style="font-family:Outfit,sans-serif;font-weight:600;font-size:13px;margin:0 0 2px">${esc(a.label)}</p>
                <span style="font-size:9px;text-transform:uppercase;letter-spacing:1px;color:${catColor(a.category)}">${esc(catLabel(a.category))}</span>
                ${a.note ? `<p style="font-size:12px;color:#8c94a8;margin:4px 0 6px">${esc(a.note)}</p>` : "<div style='height:6px'></div>"}
                <p style="font-size:10px;color:#8c94a8;margin:0 0 6px">${a.lat.toFixed(2)}, ${a.lon.toFixed(2)}</p>`;
            const btn = document.createElement("button");
            btn.textContent = "Delete pin";
            btn.style.cssText = "font-size:11px;color:#ef4444;background:transparent;border:1px solid #ef444455;border-radius:6px;padding:3px 8px;cursor:pointer";
            btn.onclick = async () => { await api.delete(`/projects/${activeProject.id}/annotations/${a.id}`); await refreshProjects(); toast.success("Pin removed"); };
            popup.appendChild(btn);
            m.bindPopup(popup);
            m.addTo(lg);
        });
    }, [annotations, activeProject, layers.annotations, refreshProjects]);
    useEffect(() => { drawAnnotations(); }, [drawAnnotations]);

    // measure layer
    useEffect(() => {
        const lg = measureLayerRef.current;
        if (!lg) return;
        lg.clearLayers();
        if (measurePts.length === 0) return;
        L.polyline(measurePts, { color: "#ffb703", weight: 2, dashArray: "6 6" }).addTo(lg);
        measurePts.forEach((p, i) => {
            L.circleMarker(p, { radius: 4, color: "#ffb703", fillColor: "#08090d", fillOpacity: 1, weight: 2 }).addTo(lg);
            if (i > 0) {
                const seg = marsDistanceKm(measurePts[i - 1], p);
                const mid = [(measurePts[i - 1][0] + p[0]) / 2, (measurePts[i - 1][1] + p[1]) / 2];
                L.marker(mid, { icon: L.divIcon({ className: "ares-region-marker", html: `<span style="font-family:JetBrains Mono,monospace;font-size:10px;color:#ffb703;background:rgba(8,9,13,0.85);padding:1px 5px;border-radius:4px;white-space:nowrap">${seg.toFixed(0)} km</span>`, iconSize: [60, 16] }) }).addTo(lg);
            }
        });
    }, [measurePts]);

    const setModeExclusive = (m) => {
        setMode((cur) => (cur === m ? null : m));
        if (m !== "measure") setMeasurePts([]);
    };

    const saveAnnotation = async () => {
        if (!form.label.trim() || !pending || !activeProjectId) return;
        try {
            await api.post(`/projects/${activeProjectId}/annotations`, { label: form.label, note: form.note, category: form.category, lat: pending.lat, lon: pending.lng });
            await refreshProjects();
            toast.success("Annotation pinned to project");
            setPending(null);
            setMode(null);
        } catch (e) { toast.error("Could not save annotation"); }
    };

    return (
        <div className="flex flex-col lg:flex-row gap-5">
            <div className="lg:flex-1 flex flex-col gap-3">
                {/* Toolbar */}
                <div className="flex flex-wrap items-center gap-3 rounded-xl border border-ares-border bg-ares-dark/90 px-4 py-3">
                    <Select value={activeProjectId} onValueChange={(v) => { setActiveProjectId(v); setMode(null); }}>
                        <SelectTrigger className="w-48 h-9 bg-ares-panel border-ares-border" data-testid="annotation-project-select">
                            <SelectValue placeholder={projects.length ? "Select project…" : "No projects yet"} />
                        </SelectTrigger>
                        <SelectContent className="bg-ares-panel border-ares-border">
                            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button onClick={() => setModeExclusive("add")} disabled={!activeProjectId}
                        className={mode === "add" ? "h-9 bg-ares-cyan text-ares-void hover:bg-ares-cyan/80" : "h-9 bg-ares-orange hover:bg-[#e55a2b] text-white"} data-testid="toggle-add-pin-btn">
                        {mode === "add" ? <><Crosshair className="w-4 h-4 mr-1.5" /> Click map</> : <><MapPin className="w-4 h-4 mr-1.5" /> Add Pin</>}
                    </Button>
                    <Button onClick={() => setModeExclusive("measure")} variant="outline"
                        className={mode === "measure" ? "h-9 border-ares-amber text-ares-amber bg-ares-amber/10" : "h-9 border-ares-border bg-transparent text-ares-bright hover:bg-ares-panel"} data-testid="toggle-measure-btn">
                        <Ruler className="w-4 h-4 mr-1.5" /> Measure
                    </Button>
                    {mode === "measure" && measurePts.length > 0 && (
                        <>
                            <span className="font-mono text-sm text-ares-amber" data-testid="measure-total">{measureTotal.toFixed(0)} km</span>
                            <Button variant="ghost" size="sm" className="h-9 text-ares-muted hover:text-destructive" onClick={() => setMeasurePts([])} data-testid="clear-measure-btn">Clear</Button>
                        </>
                    )}
                    {mode === "add" && (
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-ares-muted hover:text-destructive" onClick={() => setMode(null)} data-testid="cancel-add-pin-btn"><X className="w-4 h-4" /></Button>
                    )}
                    <div className="flex items-center gap-4 ml-auto">
                        {[
                            { key: "regions", label: "Regions", testid: "layer-toggle-regions" },
                            { key: "annotations", label: "Pins", testid: "layer-toggle-annotations" },
                            { key: "ice", label: "Ice", testid: "layer-toggle-ice" },
                        ].map((l) => (
                            <div key={l.key} className="flex items-center gap-2">
                                <Switch checked={layers[l.key]} onCheckedChange={(v) => setLayers((s) => ({ ...s, [l.key]: v }))} data-testid={l.testid} />
                                <Label className="font-mono text-[11px] uppercase tracking-wider text-ares-muted cursor-pointer">{l.label}</Label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Map */}
                <div className="relative rounded-xl border border-ares-border overflow-hidden hud-corners min-h-[460px]" data-testid="mars-map-canvas">
                    <div ref={mapEl} style={{ height: 500, width: "100%", cursor: mode ? "crosshair" : "grab" }} />
                    <div className="absolute top-3 left-14 glass rounded-lg px-3 py-1.5 z-[1000] pointer-events-none">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-ares-orange">Live Orbital Basemap · MOLA/MGS</p>
                    </div>
                    {layers.ice && (
                        <div className="absolute bottom-3 right-3 glass rounded-lg px-3 py-2 z-[1000] pointer-events-none">
                            <p className="font-mono text-[9px] uppercase tracking-widest text-ares-cyan flex items-center gap-1.5"><Snowflake className="w-3 h-3" /> Subsurface ice intensity</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Detail panel */}
            <aside className="lg:w-[360px] shrink-0 rounded-xl border border-ares-border bg-ares-dark/90 p-5" data-testid="region-detail-panel">
                {selected && (
                    <>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-ares-orange">{selected.quadrant}</p>
                        <h2 className="font-heading text-2xl font-semibold mt-1">{selected.name}</h2>
                        <Badge className={`mt-2 border ${evidenceBadge[selected.water_evidence] || evidenceBadge.low}`}>Water evidence: {selected.water_evidence}</Badge>
                        <p className="text-sm text-ares-muted leading-relaxed mt-4">{selected.summary}</p>
                        <div className="grid grid-cols-2 gap-3 mt-5">
                            <Stat icon={Mountain} label="Elevation" value={`${selected.elevation_m.toLocaleString()} m`} />
                            <Stat icon={Droplets} label="Ice probability" value={`${Math.round(selected.ice_probability * 100)}%`} accent="text-ares-cyan" />
                            <Stat icon={TriangleAlert} label="Mean slope" value={`${selected.slope_deg}°`} accent={selected.slope_deg > 15 ? "text-ares-amber" : undefined} />
                            <Stat icon={Ruler} label="Roughness" value={selected.roughness.toFixed(2)} />
                        </div>
                        <div className="mt-5">
                            <p className="font-mono text-[10px] uppercase tracking-widest text-ares-muted mb-2">Geological features</p>
                            <div className="flex flex-wrap gap-1.5">
                                {selected.features.map((f) => <span key={f} className="text-xs rounded-md border border-ares-border bg-ares-panel px-2 py-1 text-ares-bright/80">{f}</span>)}
                            </div>
                        </div>
                    </>
                )}

                {activeProject && (
                    <div className="mt-6 border-t border-ares-border pt-4">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-ares-orange mb-3 flex items-center gap-1.5"><Pin className="w-3.5 h-3.5" /> {activeProject.name} · {annotations.length} pins</p>
                        {annotations.length === 0 && <p className="text-xs text-ares-muted">Enable "Add Pin" and click the map to annotate this project.</p>}
                        <div className="space-y-2">
                            {annotations.map((a) => (
                                <div key={a.id} className="group rounded-lg border border-ares-border bg-ares-panel p-2.5 flex items-start justify-between gap-2" data-testid={`annotation-item-${a.id}`}>
                                    <button className="text-left min-w-0" onClick={() => mapRef.current?.setView([a.lat, a.lon], 4)}>
                                        <div className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: catColor(a.category) }} />
                                            <p className="text-sm font-medium truncate">{a.label}</p>
                                        </div>
                                        <p className="font-mono text-[10px] uppercase tracking-wider mt-0.5" style={{ color: catColor(a.category) }}>{catLabel(a.category)}</p>
                                        {a.note && <p className="text-xs text-ares-muted mt-0.5">{a.note}</p>}
                                    </button>
                                    <button onClick={async () => { await api.delete(`/projects/${activeProject.id}/annotations/${a.id}`); await refreshProjects(); toast.success("Pin removed"); }}
                                        className="text-ares-muted hover:text-destructive transition-colors shrink-0" data-testid={`delete-annotation-${a.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </aside>

            {/* Annotation dialog */}
            <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
                <DialogContent className="bg-ares-dark border-ares-border text-ares-bright">
                    <DialogHeader><DialogTitle className="font-heading">New Map Annotation</DialogTitle></DialogHeader>
                    {pending && (
                        <div className="space-y-3 py-2">
                            <p className="font-mono text-[11px] text-ares-muted">Location: {pending.lat}, {pending.lng}</p>
                            <Input placeholder="Pin label (e.g. Possible ice scarp)" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                                className="bg-ares-panel border-ares-border" data-testid="annotation-label-input" autoFocus />
                            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                                <SelectTrigger className="bg-ares-panel border-ares-border" data-testid="annotation-category-select"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-ares-panel border-ares-border">
                                    {CATEGORIES.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>
                                            <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} /> {c.label}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Textarea placeholder="Notes (optional)…" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                                className="bg-ares-panel border-ares-border resize-none h-20" data-testid="annotation-note-input" />
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setPending(null)} className="hover:bg-ares-panel">Cancel</Button>
                        <Button onClick={saveAnnotation} disabled={!form.label.trim()} className="bg-ares-orange hover:bg-[#e55a2b] text-white" data-testid="save-annotation-btn">Pin to Project</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Stat({ icon: Icon, label, value, accent }) {
    return (
        <div className="rounded-lg border border-ares-border bg-ares-panel p-3">
            <div className="flex items-center gap-1.5 text-ares-muted mb-1">
                <Icon className="w-3.5 h-3.5" />
                <span className="font-mono text-[10px] uppercase tracking-wider">{label}</span>
            </div>
            <p className={`font-heading text-lg font-semibold ${accent || "text-ares-bright"}`}>{value}</p>
        </div>
    );
}
